import { type FC, type ReactElement, useState, useRef } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { Swiper } from 'dibs-pinch-zoom-swipe';
import { useLazyLoadMedia } from 'dibs-elements/exports/useLazyLoadMedia.tsx';
import { ioParams } from 'dibs-fastly/exports/imageOptimizations.ts';
import { Carousel } from 'dibs-carousel';
import { CarouselDots } from 'dibs-carousel/exports/CarouselDots.jsx';
import { type NftPdpExhibitionNoteImageList_note$key } from './__generated__/NftPdpExhibitionNoteImageList_note.graphql.ts';

import styles from './NftPdpExhibitionNoteImageList.scss';

type Props = {
    note: NftPdpExhibitionNoteImageList_note$key;
};

const counter = (index: number, totalItems: number): ReactElement => (
    <FormattedMessage
        id="nft.pdp.exhibitionNoteImage.carouselCount"
        defaultMessage="{currentIndex} of {totalItems}"
        values={{ currentIndex: index + 1, totalItems }}
    />
);

const MAX_MEDIA_SIZE = '714';

const imgParams = ioParams({
    height: MAX_MEDIA_SIZE,
    width: MAX_MEDIA_SIZE,
    fit: 'bounds',
    'resize-filter': 'bilinear',
    format: 'pjpg',
    disable: 'upscale',
});

const staticImgString = ioParams(imgParams, { dpr: '2', auto: 'webp', optimize: 'high' }).toQuery();

const AdditionalContentImage: FC<{
    url?: string | null;
    height?: number;
    width?: number;
}> = ({ url, height, width }) => {
    const imgRef = useRef<HTMLImageElement | null>(null);
    const lazyLoad = useLazyLoadMedia('image', {
        ref: imgRef,
        width: width?.toString(),
        height: height?.toString(),
        maxHeight: '481px',
        maxWidth: `${width}px`,
        shimmer: false,
    });
    return (
        <img
            alt=""
            ref={imgRef}
            className={lazyLoad.styleProps.className}
            style={lazyLoad.styleProps.style}
            width={width}
            height={height}
            src={url + staticImgString}
            loading="eager"
        />
    );
};

export const NftPdpExhibitionNoteImageList: FC<Props> = ({ note: noteRef }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const note = useFragment(
        graphql`
            fragment NftPdpExhibitionNoteImageList_note on NftExhibitionNoteImageListContent {
                caption
                images {
                    url
                    width
                    height
                }
            }
        `,
        noteRef
    );
    const { caption, images } = note;
    const totalItems = images?.length || 0;
    const carousel = (
        <figure className={styles.carouselWrapper}>
            <div className={styles.hideOnMobile}>
                <Carousel
                    totalItems={totalItems}
                    itemsPerPage={1}
                    step={1}
                    onIndexChange={({ index }: { index: number }) => setCurrentIndex(index)}
                    renderItem={({ index }: { index: number }) => {
                        const { url, width, height } = images?.[index] || {};
                        return (
                            <div
                                key={`additional-image-${url}-${index}`}
                                className={styles.imageWrapper}
                            >
                                <AdditionalContentImage
                                    url={url}
                                    width={width || undefined}
                                    height={height || undefined}
                                />
                            </div>
                        );
                    }}
                />
                {totalItems > 1 && (
                    <div className={styles.counter}>{counter(currentIndex, totalItems)}</div>
                )}
            </div>
            <div className={styles.showOnMobile}>
                <Swiper
                    itemsPerPage={1}
                    hasInfiniteLoop
                    onPageChange={(newPhoto: number) => setCurrentIndex(newPhoto)}
                    startingPage={currentIndex}
                    classNames={{ wrapper: styles.swiperCarousel, item: styles.listItem }}
                >
                    {images?.map((image, index) => {
                        const { url, width, height } = image || {};
                        return (
                            <div
                                key={`additional-image-${url}-${index}`}
                                className={styles.imageWrapper}
                            >
                                <AdditionalContentImage
                                    url={url}
                                    width={width || undefined}
                                    height={height || undefined}
                                />
                            </div>
                        );
                    })}
                </Swiper>
                {totalItems > 1 && (
                    <CarouselDots dotsPerPage={6} index={currentIndex} totalItems={totalItems} />
                )}
            </div>
            {caption && <figcaption className={styles.caption}>{caption}</figcaption>}
        </figure>
    );
    return <div className={styles.bodyText}>{carousel}</div>;
};
